/**
 * DashboardPage Component
 *
 * This component represents the dashboard page of the application. It fetches and displays
 * active threshold violation measurements based on the user's role and company. The component
 * also provides functionality to acknowledge alerts and dynamically adjusts table headers
 * based on the user's role. Additionally, it includes a helper modal to guide users on what
 * they can do on this page.
 *
 * Key functionalities:
 * - Fetches user role from JWT token stored in localStorage.
 * - Fetches active threshold violation measurements from the API.
 * - Dynamically adjusts table headers based on user role.
 * - Provides a loading spinner while data is being fetched.
 * - Allows users to acknowledge alerts.
 * - Includes a helper modal to guide users on the page functionalities.
 */

import { jwtDecode } from "jwt-decode";
import React, { useCallback, useContext, useEffect, useState } from "react";
import UserContext from "../../context/UserContext";
import DashboardSection from "./reusable/DashboardSection";
import "./DashboardPage.scss";
import {
  getAllActiveThresholdViolationMeasurements,
  acknowledgeThresholdViolationByRecordId,
  previewAcknowledgeAllThresholdViolations,
  acknowledgeAllThresholdViolations,
} from "../../api/sensorApi";
import Spinner from "react-bootstrap/Spinner";
import { UserRoleProvider } from "../../context/userRoleContext";
import { toast } from "react-toastify";
import LoadingAnimation from "../Shared/LoadingAnimation/LoadingAnimation";
import PageHeader from "../Shared/PageHeader/PageHeader";
import useTranslation from "../../hooks/useTranslation";
import moment from "moment-timezone";
import USER_ROLES from "../../constants/userRoles";
import { useMemo } from "react";

export function GrowExample() {
  return <Spinner animation="grow" />;
}

const DashboardPage = () => {
  const { user } = useContext(UserContext);
  const companyId = user.company_id;
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [dashboardAlertItems, setDashboardAlertItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [tableHeaders, setTableHeaders] = useState({});
  const [alertFilter, setAlertFilter] = useState("all");
  const { t } = useTranslation("dashboardPage");

  // Extract user role from token outside of useEffect to prevent unnecessary re-renders
  const token = localStorage.getItem("token");
  const decodedToken = token ? jwtDecode(token) : {};
  const userRole = decodedToken.role || "";
  // Derived: only alerts owned by the current user — used for page ack button visibility
  const currentUserId = user.userId;
  // Derived: apply filter on top of fetched alerts
  const filteredAlertItems = useMemo(() => {
    if (alertFilter === "own") {
      return dashboardAlertItems.filter(
        (item) => String(item.users_id) === currentUserId,
      );
    }
    if (alertFilter === "others") {
      return dashboardAlertItems.filter(
        (item) => String(item.users_id) !== currentUserId,
      );
    }
    return dashboardAlertItems; // "all"
  }, [dashboardAlertItems, alertFilter, currentUserId]);

  // Owned items on current filtered page — used for page ack button
  const ownedItemsOnPage = useMemo(() => {
    return filteredAlertItems.filter(
      (item) => String(item.users_id) === currentUserId,
    );
  }, [filteredAlertItems, currentUserId]);

  //const hasOwnedItemsOnPage = ownedItemsOnPage.length > 0;
/*   const isSuperOrCompanyAdmin =
    userRole === USER_ROLES.SUPER_ADMIN ||
    userRole === USER_ROLES.COMPANY_ADMIN; */
    const isSuperAdmin = userRole === USER_ROLES.SUPER_ADMIN;
    const isSuperOrCompanyAdmin =
      userRole === USER_ROLES.SUPER_ADMIN ||
      userRole === USER_ROLES.COMPANY_ADMIN;
  // Define headers once and filter based on role
  const getTableHeaders = useCallback((t, role) => {
    const baseHeaders = {
      probeid: t("probeId"),
      sens_num: t("sensNum"),
      containername: t("container"),
      sitename: t("site"),
      sens_timestamp: t("sensTimestamp"),
      sens_value: t("measuredValue"),
      min_value: t("minAlertValue"),
      max_value: t("maxAlertValue"),
      user: t("user"),
      companyname: t("companyName"),
      moreInfoModalButton: "",
      acknowledgedButton: "",
    };

    // Filter headers based on user role
    if (role === USER_ROLES.COMPANY_ADMIN) {
      delete baseHeaders.companyname;
    } else if (role === USER_ROLES.COMPANYUSER) {
      delete baseHeaders.sitename;
      delete baseHeaders.companyname;
      delete baseHeaders.user;
    }
    return baseHeaders;
  }, []);

  // Fetch translations and set headers when role or translations change
  useEffect(() => {
    if (userRole) {
      const headers = getTableHeaders(t, userRole);
      setTableHeaders(headers);
    }
  }, [userRole, t, getTableHeaders]);

  // Fetch alert items
  const fetchAlertItems = useCallback(async () => {
    if (user && user.token) {
      try {
        const response = await getAllActiveThresholdViolationMeasurements(
          currentPage,
          itemsPerPage,
        );

        if (response && response.alerts) {
          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

          const alertsArray = Array.isArray(response.alerts)
            ? response.alerts
            : (response.alerts.alerts ?? []);

          const adjustedAlerts = alertsArray.map((alert) => {
            const adjustedTimestamp = moment
              .utc(alert.sens_timestamp)
              .tz(userTimezone)
              .format();
            return { ...alert, sens_timestamp: adjustedTimestamp };
          });

          setDashboardAlertItems(adjustedAlerts);
          setTotalItems(response.total || 0);
        } else {
          setDashboardAlertItems([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error(error);
      }
    }
  }, [user, companyId, currentPage, itemsPerPage]);

  useEffect(() => {
    if (user && user.token) {
      fetchAlertItems();
      setIsPageLoaded(true);
    }
  }, [user, companyId, fetchAlertItems, itemsPerPage]);

  // Handle acknowledgment of threshold violation
  const handleAcknowledge = async (thresholdViolationRecord) => {
    const recordId = thresholdViolationRecord._id;
    const recordOwnerId = thresholdViolationRecord.users_id; // need this from backend
    const currentUserId = user.userId;

    // Prevent cross-user acknowledgement on frontend as well
    if (String(recordOwnerId) !== String(currentUserId)) {
      toast.error(
        t("dashboardAcknowledgeNotOwner") ||
          "Csak a saját riasztásait tudja tudomásul venni.",
      );
      return;
    }

    try {
      await acknowledgeThresholdViolationByRecordId(recordId);
      fetchAlertItems();
      toast.success(t("dashboardItemApproveSuccess"));
    } catch (error) {
      console.error(error);
      toast.error(t("dashboardItemApproveError"));
    }
  };

  const handleAcknowledgeCurrentPage = async () => {
    try {
      // Reuse derived value from component top — no need to recalculate
      const recordIds = ownedItemsOnPage.map((item) => item._id);

      if (recordIds.length === 0) {
        toast.warn(
          t("dashboardNoItemsToApprove") ||
            "Nincs tudomásul vehető saját riasztás ezen az oldalon.",
        );
        return;
      }

      const preview = await previewAcknowledgeAllThresholdViolations(recordIds);

      // preview.count reflects only owned records the backend confirmed are unacked
      // could differ from recordIds.length if some were already acked in the meantime
      if (preview.count === 0) {
        toast.info(
          t("dashboardNoItemsToApprove") ||
            "Nincs tudomásul vehető saját riasztás ezen az oldalon.",
        );
        return;
      }

      const rangeFrom = preview.rangeFrom
        ? new Date(preview.rangeFrom).toLocaleString()
        : "N/A";
      const rangeTo = preview.rangeTo
        ? new Date(preview.rangeTo).toLocaleString()
        : "N/A";

      const confirmed = window.confirm(
        `${preview.count} ${t("acknowledgeOwnItems")}\n` +
          `${t("acknowledgeTimerange")} ${rangeFrom} - ${rangeTo}\n\n` +
          `${t("acknowledgeContinue")}`,
      );

      if (!confirmed) return;

      await acknowledgeAllThresholdViolations(recordIds);
      fetchAlertItems();
      toast.success(t("dashboardAllPageApproveSuccess"));
    } catch (error) {
      console.error(error);
      toast.error(t("dashboardItemApproveError"));
    }
  };

  const handleAcknowledgeAll = async () => {
    try {
      // Preview scoped to current user only (backend enforces users_id = acknowledgedBy)
      // Empty array = bulk preview, returns count + range for ALL unacked records of this user
      const preview = await previewAcknowledgeAllThresholdViolations([]);

      if (preview.count === 0) {
        toast.info(
          t("dashboardNoItemsToApprove") ||
            "Nincs tudomásul vehető saját riasztás.",
        );
        return;
      }

      const rangeFrom = preview.rangeFrom
        ? new Date(preview.rangeFrom).toLocaleString()
        : "N/A";
      const rangeTo = preview.rangeTo
        ? new Date(preview.rangeTo).toLocaleString()
        : "N/A";

      const confirmed = window.confirm(
        `${t("acknowledgeAllConfirm1")}\n\n` +
          `${t("acknowledgeAllConfirm2")} ${preview.count}\n` +
          `${t("acknowledgeAllConfirm3")} ${rangeFrom}\n` +
          `${t("acknowledgeAllConfirm4")} ${rangeTo}\n\n` +
          `${t("acknowledgeAllConfirm5")}`,
      );

      if (!confirmed) return;

      // Empty recordIds = bulk ack, backend scopes to users_id = current user
      await acknowledgeAllThresholdViolations([]);
      fetchAlertItems();
      toast.success(t("dashboardAllApproveSuccess"));
    } catch (error) {
      console.error(error);
      toast.error(t("dashboardItemApproveError"));
    }
  };

  // Handle setting the number of items per page
  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  return (
    <UserRoleProvider>
      <>
        {!isPageLoaded && <LoadingAnimation />}
        {isPageLoaded && (
          <div className="container dashboard-page__container navbar-margin">
            <PageHeader
              title={t("dashboard_title")}
              helperTitle={t("dashboard_title")}
              helperBody={t("dashboard_helper")}
            />
            <div className="dashboard__grid">
              {isSuperOrCompanyAdmin && (
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="fw-semibold text-muted small me-1">
                    {t("alertFilterLabel")}
                  </span>
                  {[
                    {
                      key: "all",
                      label: t("alertFilterAll"),
                      color: "primary",
                    },
                    {
                      key: "own",
                      label: `${t("alertFilterOwn")}`,
                      color: "success",
                    },
                    {
                      key: "others",
                      label: t("alertFilterOthers"),
                      color: "warning",
                    },
                  ].map(({ key, label, color }) => (
                    <span
                      key={key}
                      onClick={() => setAlertFilter(key)}
                      className={`badge rounded-pill px-3 py-2 cursor-pointer ${
                        alertFilter === key
                          ? `bg-${color} text-white`
                          : `bg-${color} bg-opacity-10 text-${color}`
                      }`}
                      style={{ cursor: "pointer", fontSize: "0.8rem" }}
                    >
                      {label}
                    </span>
                  ))}
                  {alertFilter !== "all" && (
                    <span className="text-muted small ms-1">
                      {filteredAlertItems.length} / {dashboardAlertItems.length}
                    </span>
                  )}
                </div>
              )}
              <DashboardSection
                tableHeaders={tableHeaders}
                title={t("dashboard_table_title")}
                items={filteredAlertItems}
                linkTo="/alerts"
                handleAcknowledge={handleAcknowledge}
                handleAcknowledgeCurrentPage={handleAcknowledgeCurrentPage}
                handleAcknowledgeAll={handleAcknowledgeAll}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                handleItemsPerPageChange={handleItemsPerPageChange}
                totalItems={totalItems}
              />
              {/*           <DashboardSection
            tableHeaders={tableHeaders}
            title="Jelzések"
            items={dashboardWarningItems}
            linkTo="/warnings"
          /> */}
              {/*             <DashboardSection
              tableHeaders={locationTableHeaders}
              title="Helyszínek"
              items={dashboardLocationItems}
              linkTo="/locations"
            /> */}
            </div>
          </div>
        )}
      </>
    </UserRoleProvider>
  );
};

export default DashboardPage;
